import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';

export const ASSET_URLS = {
  MODELS: {
    CHAINSAW: '/assets/models/chainsaw-v1.glb',
    MAN: '/assets/models/man1.glb',
    PINE_TREE: '/assets/models/pine_tree.glb',
    TREE_BRANCH: '/assets/models/tree_branch.glb'
  },
  TEXTURES: {
    BARK_WILLOW: '/assets/textures/bark_willow_02_diff_4k.jpg',
    ENDCAP: '/assets/textures/endcap.jpg',
    MUD: '/assets/textures/brown_mud_02_diff_4k.jpg',
    LEAVES: '/assets/textures/brown_mud_leaves_01_diff_4k.jpg',
    SAWBUCK: '/assets/textures/support_sawbuck.jpg'
  }
};
export class AssetManager {
  public static textures = new Map<string, THREE.Texture>();
  public static models = new Map<string, { scene: THREE.Group, animations: THREE.AnimationClip[] }>();

  public static async loadAll(extraModelUrls: string[] = []): Promise<void> {
    const textureLoader = new THREE.TextureLoader();
    const gltfLoader = new GLTFLoader();

    // Setup DRACOLoader for compressed GLTF files
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
    gltfLoader.setDRACOLoader(dracoLoader);

    // List of assets from specs/modules/12_asset_manager.md
    const textureUrls = [
      ASSET_URLS.TEXTURES.BARK_WILLOW,
      ASSET_URLS.TEXTURES.ENDCAP,
      ASSET_URLS.TEXTURES.MUD,
      ASSET_URLS.TEXTURES.LEAVES,
      ASSET_URLS.TEXTURES.SAWBUCK
    ];

    const modelUrls = [
      ASSET_URLS.MODELS.CHAINSAW,
      ASSET_URLS.MODELS.MAN,
      ASSET_URLS.MODELS.PINE_TREE,
      ASSET_URLS.MODELS.TREE_BRANCH
    ];

    for (const url of extraModelUrls) {
      if (!modelUrls.includes(url)) {
        modelUrls.push(url);
      }
    }

    const loadPromises: Promise<void>[] = [];

    // Load Textures
    for (const url of textureUrls) {
      loadPromises.push(
        new Promise((resolve) => {
          textureLoader.load(
            url,
            (texture) => {
              texture.colorSpace = THREE.SRGBColorSpace;
              this.textures.set(url, texture);
              resolve();
            },
            undefined,
            (err) => {
              console.warn(`Failed to load texture ${url}`, err);
              // Resolve anyway so the game doesn't hang if an asset is missing
              resolve();
            }
          );
        })
      );
    }

    // Load Models
    for (const url of modelUrls) {
      loadPromises.push(
        new Promise((resolve) => {
          gltfLoader.load(
            url,
            (gltf) => {
              this.models.set(url, { scene: gltf.scene, animations: gltf.animations });
              resolve();
            },
            undefined,
            (err) => {
              console.warn(`Failed to load model ${url}`, err);
              resolve();
            }
          );
        })
      );
    }

    await Promise.all(loadPromises);
    console.log('AssetManager: All assets loaded.');
  }

  public static getTexture(url: string): THREE.Texture | undefined {
    return this.textures.get(url);
  }

  public static getModel(url: string): { scene: THREE.Group, animations: THREE.AnimationClip[] } | undefined {
    return this.models.get(url);
  }
}
